import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { authorizedGet, authorizedPost, authorizedPut } from "../../../Config/networkWithToken";
import { config } from "../../../utils/helper";
import configuration from "../../../Config/configuration";

export const favouritesQueryKey = (adminId) => ["favourites", adminId];
export const getProfileId = (profile) => profile?._id || profile?.userId;

const fetchFavourites = async (adminId) => {
  if (!adminId) return [];
  const res = await authorizedGet(
    `${configuration.apis.getFavouriteUsers}?adminId=${adminId}`,
    config,
  );
  const response = res?.data;
  if (!response?.status) throw new Error(response?.message || "Failed to fetch favourites");
  return response?.data ?? [];
};


export const useFavourites = (adminId, options = {}) => {
  return useQuery({
    queryKey: favouritesQueryKey(adminId),
    queryFn: () => fetchFavourites(adminId),
    enabled: !!adminId && options.enabled !== false,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useToggleFavourite = (adminId) => {
  const queryClient = useQueryClient();
  const queryKey = favouritesQueryKey(adminId);

  return useMutation({
    mutationFn: async ({ profileId, isFavourite }) => {
      if (isFavourite) {
        const res = await authorizedPut(
          configuration.apis.updateFavouriteUser,
          { adminId, userId: profileId, isFavouriteUser: false },
          config,
        );
        const response = res?.data;
        if (!response?.status) throw new Error(response?.message || "Failed to remove favourite");
        return response;
      }

      const markRes = await authorizedPost(
        configuration.apis.markFavouriteUser,
        { adminId, userId: profileId },
        config,
      );
      const markResponse = markRes?.data;
      if (markResponse?.status) return markResponse;

      const updateRes = await authorizedPut(
        configuration.apis.updateFavouriteUser,
        { adminId, userId: profileId, isFavouriteUser: true },
        config,
      );
      const updateResponse = updateRes?.data;
      if (!updateResponse?.status) {
        throw new Error(
          updateResponse?.message || markResponse?.message || "Failed to add favourite",
        );
      }
      return updateResponse;
    },

    onSuccess: (response) => {
      if (Array.isArray(response?.data)) {
        queryClient.setQueryData(queryKey, response.data);
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};
